import { parse } from 'csv-parse/browser/esm/sync';
import JSON5 from 'json5';
import { get, isPlainObject } from 'lodash-es';
import { ParsedField } from 'src/features/reports/CustomReportsRoute/FieldListEditor';

function collectDotPaths(obj: unknown, prefix = ''): string[] {
  if (!isPlainObject(obj)) return prefix ? [prefix] : [];
  const paths: string[] = [];
  for (const key of Object.keys(obj as Record<string, unknown>)) {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    const child = (obj as Record<string, unknown>)[key];
    if (isPlainObject(child)) {
      paths.push(...collectDotPaths(child, fullKey));
    } else {
      paths.push(fullKey);
    }
  }
  return paths;
}

function csvCell(value: unknown): string {
  if (value === null || value === undefined) return '""';
  if (typeof value === 'string') return JSON.stringify(value);
  return JSON.stringify(String(value));
}

export function transformCustomTripReport(fields: ParsedField[], data: string): Blob {
  const records: Record<string, string>[] = parse(data, {
    columns: true,
  });

  // First pass: collect all dot-notation paths for each expanded field across all rows
  const expandedPaths: Record<string, string[]> = {};
  for (const field of fields) {
    if (field.expandedType !== 'json') continue;
    const seen = new Set<string>();
    for (const record of records) {
      const raw = record[field.value];
      if (!raw) continue;
      try {
        const parsed = JSON5.parse(raw);
        for (const path of collectDotPaths(parsed)) {
          seen.add(path);
        }
      } catch {
        // unparseable value — treat as a plain cell
      }
    }
    expandedPaths[field.value] = Array.from(seen);
  }

  // Build header row
  const headerCells: string[] = [];
  for (const field of fields) {
    const paths = expandedPaths[field.value];
    if (field.expandedType === 'json' && paths?.length) {
      for (const path of paths) {
        headerCells.push(`${field.label}:${path}`);
      }
    } else {
      headerCells.push(field.label);
    }
  }

  // Build data rows
  const dataRows = records.map((record) => {
    const cells: string[] = [];
    for (const field of fields) {
      const paths = expandedPaths[field.value];
      if (field.expandedType === 'json' && paths?.length) {
        let parsed: unknown = null;
        try {
          parsed = JSON5.parse(record[field.value] ?? '');
        } catch {
          // leave parsed as null
        }
        for (const path of paths) {
          cells.push(csvCell(parsed !== null ? get(parsed, path) : undefined));
        }
      } else {
        cells.push(csvCell(record[field.value]));
      }
    }
    return cells.join(',');
  });

  return new Blob([`${headerCells.join(',')}\n${dataRows.join('\n')}`], {
    type: 'text/csv',
  });
}
